/* Fantasy Forged — Deck Search identity + focus, per catalogue.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE:
 *   the card NAME is the search. A collector number / card code only ever adds
 *   FOCUS to results the name already found. It never goes into the query.
 *
 * Why: sections/deck-search.liquid built its query as `name + " " + collector`.
 * For a PTCG Live line "3 Water Energy SVE 3" that searches `Water Energy 3`,
 * unquoted, and Shopify tokenizes it — the bare "3" matches any card with a 3
 * anywhere, so the pool filled with unrelated cards that happened to carry that
 * number. Measured against the live catalogue: a name-only search returns the
 * right card 6/6 for Relicanth, 6 real printings for Water Energy, and exact
 * hits for Boss's Orders and Lana's Aid. The catalogue was never the problem.
 *
 * Apostrophes MUST be quoted. Unquoted "Lana's Aid" sprays into MTG (Planar
 * Incision, Monastery Raid) and after game-scoping NOTHING survives — the pool
 * goes empty and whatever is left gets shown. Quoted, it returns the exact card.
 *
 * FOCUS IS PER CATALOGUE, and the identifier is a different THING in each one.
 * All shapes below are taken from real Fantasy Forged product data, not guessed.
 */
(function (global) {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  function norm(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Same game key deck-search.liquid uses: "MTG Singles" -> "mtg". */
  function gameKey(type) {
    return String(type || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/\s*singles?\s*$/, '')
      .trim();
  }

  /**
   * Per-catalogue focus.
   *
   *   kind 'collector' — a printing number, "41/181" or "215/204". Compared on
   *                      the NUMERATOR only, de-padded, because a decklist may
   *                      write 007, 7 or 7/102 for one card.
   *   kind 'cardId'    — a set-scoped card code, "OP07-117" / "ST01-001".
   *   kind 'cardCode'  — a printing code, "DUPO-EN012". Yu-Gi-Oh's identifier is
   *                      a CODE, not a number, and it sits where every other
   *                      game puts the set name (see readCardCode).
   *
   * A catalogue that is ABSENT here is name-first with no focus at all. That is
   * deliberate: for MTG, Flesh and Blood, Star Wars and Riftbound the name plus
   * set is already decisive, and their in-title numbers are unreliable — real
   * MTG titles here carry no collector number whatsoever ("Siren Lookout
   * [Ixalan]"), so demanding one would break a catalogue that currently works.
   */
  var FOCUS = {
    'pokemon': { kind: 'collector' },
    'pokemon japanese': { kind: 'collector' },
    'lorcana': { kind: 'collector' },
    'one piece': { kind: 'cardId' },
    'gundam': { kind: 'cardId' },
    'yugioh': { kind: 'cardCode' },
  };

  function focusFor(type) {
    return FOCUS[gameKey(type)] || null;
  }

  /* ------------------------------------------------- identifier extraction */

  /**
   * Collector number, numerator only, unpadded. "007/102" -> "7", "41" -> "41".
   * Non-numeric printings ("11/P1" promos) keep their numerator, "11".
   */
  function collNum(value) {
    var m = String(value == null ? '' : value).match(/(\d+)/);
    return m ? String(parseInt(m[1], 10)) : '';
  }

  /**
   * Pokemon / Lorcana: "Name (41/181) [Set]".
   *
   * Taken from the LAST collector-shaped parenthetical, not the first: titles
   * carry other parentheticals — "(Prism Star)", "(Enchanted)", "(GenCon 2023)"
   * — and on Lorcana the card name itself contains " - " subtitles. Only a group
   * that looks like a number (optionally /total) counts.
   */
  function readCollector(title) {
    var t = String(title || '').replace(/\s*\[[^\]]*\]\s*$/, ''); // drop trailing [Set]
    var re = /\((\d+(?:\/[0-9A-Za-z]+)?)\)/g;
    var last = '';
    var m;
    while ((m = re.exec(t))) last = m[1];
    return last;
  }

  /**
   * One Piece / Gundam: a set-scoped card code.
   *
   * ONE PIECE OFTEN HAS NO ID IN THE TITLE AT ALL — "Egghead [500 Years in the
   * Future]" — so the SKU is the authority and the title is the fallback.
   * Gundam is the reverse: the title carries "(ST01-001)" cleanly while the SKU
   * concatenates it as "ST01-ST01001", so the title is preferred there.
   */
  function readCardId(title, sku) {
    var ID = /\b([A-Z]{1,4}\d{2}-\d{2,3})\b/;          // OP07-117, ST01-001, GD02-071
    var PROMO = /\b(P-\d{2,3})\b/;                      // P-047
    var tt = String(title || '').toUpperCase();
    var ss = String(sku || '').toUpperCase();
    var m = tt.match(ID) || tt.match(PROMO) || ss.match(ID) || ss.match(PROMO);
    return m ? m[1] : '';
  }

  /**
   * Yu-Gi-Oh: "Performapal Smile Sorcerer [DUPO-EN012] Ultra Rare".
   *
   * TWO THINGS MAKE THIS DIFFERENT, and both break the generic parser:
   *  1. the bracket holds a CODE, not a set name — every other catalogue puts
   *     the set there;
   *  2. the bracket is NOT at the end, the rarity follows it. deck-search.liquid
   *     only matches a TRAILING [..], so today Yu-Gi-Oh yields no set AND no
   *     collector — it is matched on bare name alone, which is why a card
   *     reprinted across a dozen sets cannot be pinned down.
   * Codes seen live: DUPO-EN012, SDSB-EN032, SAST-EN062, SS02-ENV02.
   */
  function readCardCode(title, sku) {
    var CODE = /\b([A-Z0-9]{2,5}-[A-Z]{2}[A-Z0-9]?\d{2,3})\b/;
    var m = String(title || '').toUpperCase().match(CODE);
    if (m) return m[1];
    m = String(sku || '').toUpperCase().match(CODE);
    return m ? m[1] : '';
  }

  /** The focus identifier for one PRODUCT, in whatever form its catalogue uses. */
  function productFocus(type, title, sku) {
    var f = focusFor(type);
    if (!f) return '';
    if (f.kind === 'collector') return collNum(readCollector(title) || sku);
    if (f.kind === 'cardId') return readCardId(title, sku);
    if (f.kind === 'cardCode') return readCardCode(title, sku);
    return '';
  }

  /** The focus identifier the customer's decklist LINE supplies, if any. */
  function lineFocus(type, line) {
    var f = focusFor(type);
    if (!f) return '';
    if (f.kind === 'collector') return collNum(line && line.collector);
    var raw = (line && (line.cardCode || line.cardId || line.collector || line.set)) || '';
    if (f.kind === 'cardId') return readCardId(raw, raw);
    // A Yu-Gi-Oh code usually arrives still GLUED TO THE NAME. deck-search.liquid's
    // parser has no rule that splits it off: its card-ID rule wants
    // <letters><digits>-<digits> ("OP16-079") and a YGO code is
    // <letters>-<letters><digits> ("DUPO-EN106"), so none of its four branches fire
    // and the whole string stays in line.name. Reading it off the name here means
    // that file needs no fifth parse rule.
    if (f.kind === 'cardCode') return readCardCode(raw, raw) || readCardCode(line && line.name, '');
    return '';
  }

  /**
   * The card name with its own identifier removed, for catalogues that carry one
   * inline. "Decode Talker DUPO-EN106" -> "Decode Talker".
   *
   * Only strips a token THIS catalogue uses as an identifier, so a name that merely
   * looks code-ish in some other game is left untouched. Falls back to the original
   * if stripping would empty the name.
   */
  function stripFocus(type, name) {
    var f = focusFor(type);
    var s = String(name == null ? '' : name).trim();
    if (!f || f.kind !== 'cardCode') return s;
    var code = readCardCode(s, '');
    if (!code) return s;
    var out = s
      .replace(new RegExp('\\[?\\s*' + code + '\\s*\\]?', 'i'), ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return out || s;
  }

  /**
   * One call for deck-search.liquid: what to search for, and what to focus on.
   * The name drives the query; the identifier never enters it.
   */
  function prepare(type, line) {
    return {
      name: stripFocus(type, line && line.name),
      query: buildQuery(stripFocus(type, line && line.name)),
      wantFocus: lineFocus(type, line),
    };
  }

  /* ------------------------------------------------------------- the query */

  /**
   * The search string. Name only, always quoted.
   * Quoting is not cosmetic — see the apostrophe note at the top of this file.
   */
  function buildQuery(name) {
    return '"' + String(name || '').replace(/"/g, ' ').trim() + '"';
  }

  /* ------------------------------------------------------------- scoring */

  /**
   * The title up to the first "(" or "[" — i.e. the card name, before the set,
   * number and rarity, which every catalogue here puts inside one or the other.
   */
  function headName(title) {
    return String(title == null ? '' : title).split(/[([]/)[0].trim();
  }

  /**
   * Name agreement, 0..100. ZERO IS DISQUALIFYING — see chooseBest.
   * This is the floor the old scorer lacked: it awarded +80 for a matching
   * number with no name agreement at all, so an unrelated card that shared a
   * number could win outright.
   *
   * The 100 tier keys off headName, not the whole title, and that is load-bearing:
   * a plain prefix test scores "Decode Talker" and "Decode Talker Heatsoul"
   * IDENTICALLY, so with no code given the tie fell through to cheapest and could
   * buy the wrong card. Comparing against the head makes an exact card name beat a
   * longer card that merely starts with it. Same trap as "Deep Sea Diver" vs
   * "Deep Sea Diver's Log", and it exists in every catalogue, not just Yu-Gi-Oh.
   */
  function nameScore(cardName, productTitle) {
    var want = norm(cardName);
    var have = norm(productTitle);
    if (!want || !have) return 0;
    if (norm(headName(productTitle)) === want) return 100;
    if (have === want) return 100;
    if (have.indexOf(want + ' ') === 0) return 90;   // "Mareep 41 181 ..."
    if (have.indexOf(want) === 0) return 80;
    if (have.indexOf(' ' + want + ' ') !== -1) return 60;
    if (have.indexOf(want) !== -1) return 45;
    return 0;
  }

  function sameFocus(a, b) {
    if (!a || !b) return false;
    return String(a).toUpperCase() === String(b).toUpperCase();
  }

  /**
   * Rank candidates the NAME already found. Focus only reorders within them.
   * Never pass a pool here that was fetched using the number.
   */
  function rank(candidates, line, opts) {
    opts = opts || {};
    var type = opts.type || (candidates[0] && candidates[0].type) || '';
    var wantFocus = lineFocus(type, line);
    var wantName = stripFocus(type, line && line.name);

    return (candidates || [])
      .map(function (c) {
        var ns = nameScore(wantName, c.title);
        var pf = productFocus(c.type || type, c.title, c.sku);
        var score = ns;
        // Focus is a TIE-BREAK among name matches, never a substitute for one.
        if (ns > 0 && wantFocus && sameFocus(pf, wantFocus)) score += 50;
        if (ns > 0 && c.available) score += 5;
        return { candidate: c, nameScore: ns, focus: pf, score: score };
      })
      .filter(function (r) { return r.nameScore > 0; })   // THE NAME FLOOR
      .sort(function (a, b) { return b.score - a.score; });
  }

  /**
   * Pick one, or ask.
   *
   * DEFAULT IS THE CHEAPEST IN-STOCK PRINTING, and that is not a fallback — it is
   * what a deck builder actually wants. The whole feature is "paste a list, add
   * what is in stock"; they are assembling a deck, not collecting. Where one name
   * spans a wide price range the expensive end is a collector printing that
   * happens to share the name.
   *
   * AN EARLIER VERSION OF THIS FUNCTION GOT THAT BACKWARDS. It refused to pick
   * when printings differed a lot in price, on the theory that a big spread means
   * the choice matters. Measured against real Fantasy Forged stock, Water Energy
   * runs $0.35 to $25.60 — a 73x spread on the most common line in any Pokemon
   * decklist, whose answer is obviously the 35c one. The rule fired hardest
   * exactly where it was least wanted. Spread is a reason to be CONFIDENT about
   * picking cheap, not a reason to interrupt.
   *
   * The one case still worth a human glance is when even the cheapest copy is
   * expensive: the line costs real money whichever printing is chosen, so it is
   * worth showing rather than quietly adding.
   */
  function chooseBest(ranked, opts) {
    opts = opts || {};
    var confirmAbove = opts.confirmAbove == null ? 20 : opts.confirmAbove;

    if (!ranked.length) return { status: 'not_found', pick: null, ranked: ranked };

    // An explicit focus is the shopper telling us which printing they want.
    if (opts.wantFocus) {
      var exact = ranked.filter(function (r) { return sameFocus(r.focus, opts.wantFocus); });
      if (exact.length) return { status: 'ok', pick: exact[0], ranked: ranked, reason: 'focus' };
    }

    // Cheapest printing OF THIS CARD — so narrow to the best name tier BEFORE sorting
    // on price, otherwise the cheapest weaker match wins. "Decode Talker Heatsoul" at
    // $0.50 is not a cheap printing of "Decode Talker", it is a different card, and
    // ranking it last does not save it when the next step sorts purely on price.
    var topName = 0;
    ranked.forEach(function (r) { if (r.nameScore > topName) topName = r.nameScore; });
    var sameName = ranked.filter(function (r) { return r.nameScore === topName; });

    var inStock = sameName.filter(function (r) { return r.candidate.available; });
    var pool = inStock.length ? inStock : sameName;

    var cheapest = pool.slice().sort(function (a, b) {
      var pa = Number(a.candidate.price);
      var pb = Number(b.candidate.price);
      return (isFinite(pa) ? pa : Infinity) - (isFinite(pb) ? pb : Infinity);
    })[0];

    var price = Number(cheapest && cheapest.candidate.price);
    if (isFinite(price) && price >= confirmAbove) {
      return { status: 'confirm', pick: cheapest, ranked: ranked, reason: 'expensive', price: price };
    }

    return { status: 'ok', pick: cheapest || pool[0], ranked: ranked, reason: 'cheapest' };
  }

  var api = {
    norm: norm,
    gameKey: gameKey,
    focusFor: focusFor,
    collNum: collNum,
    readCollector: readCollector,
    readCardId: readCardId,
    readCardCode: readCardCode,
    productFocus: productFocus,
    lineFocus: lineFocus,
    stripFocus: stripFocus,
    prepare: prepare,
    buildQuery: buildQuery,
    headName: headName,
    nameScore: nameScore,
    rank: rank,
    chooseBest: chooseBest,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else global.FFDeckMatch = api;
})(typeof window !== 'undefined' ? window : globalThis);
